import React from "react";

import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { CardView } from "common/views/card/Card.view";
import { RowsView } from "common/views/rows/Rows.view";
import { FragmentLayout } from "core/components/FragmentLayout";
import { FragmentProvider } from "core/components/FragmentProvider";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { IQueryRoot, QueryRoot } from "core/decorators/QueryRoot";
import { Field, Operation } from "typegraph";
import * as catalogActions from "./catalogActions";
import ProgramDetailsPanelConfig from "./config/ProgramDetailsPanelConfig";
import Badge from "./entities/Badge";
import Course from "./entities/Course";
import Program from "./entities/Program";
import ProgramTrack from "./entities/ProgramTrack";
import * as badgeDisplay from "./fragments/badge/displayFragments";
import * as parentContainerFragments from "./fragments/container/parentContainerFragments";
import * as courseActionFragments from "./fragments/course/actionFragments";
import * as courseDisplayFragments from "./fragments/course/displayFragments";
import * as programDisplayFragments from "./fragments/program/displayFragments";
import * as trackDisplayFragments from "./fragments/programTrack/displayFragments";
import * as trackProductDisplayFragments from "./fragments/trackProduct/displayFragments";
import labelProvider from "./Program.labels";

/**
 * panel for rendering any details about a particular program and tracks/courses inside of it. uses fragment layouts
 *
 * @author chills
 */

const { Labels } = labelProvider;

class ProgramQuery {

	@Field({
		entity: Program,
		params: (data: any) => ({params: {id: data.id}})
	})
	public program: Program | null;

}

interface IProgramDetailsPanelState {
	expanded: { [id: string]: boolean; };
}

const trackProductFragments = {
	...courseDisplayFragments,
	...courseActionFragments,
	...trackProductDisplayFragments
};

const testFragments = {
	...courseDisplayFragments,
	...courseActionFragments
};

const ContainerFragments = FragmentProvider.forFragments("program:courses:container", parentContainerFragments);

@Panel({
	id: "program-details",
	configClass: ProgramDetailsPanelConfig,
	labelProvider,
	pageParams: true
})
@QueryRoot(ProgramQuery, (config: ProgramDetailsPanelConfig, pageParams, user) => {

	const params: any = {
		id: config.programId || pageParams.programId,
		loadBadges: true,
		loadTracks: true,
		loadCourses: true,
		loadTests: true,
		loadPretestTags: true
	};

	if (config.enableLanguageSupport) {
		params.language = user.language;
	}

	return params;
})
class ProgramDetailsPanel extends React.Component<IPanelProps<ProgramDetailsPanelConfig> & IQueryRoot<ProgramQuery>, IProgramDetailsPanelState> {

	public state = { expanded: { } };

	public render() {
		const { config, queryData, queryState } = this.props;

		if (queryState.loading) {
			return <Loading />;
		}

		if (!queryData || !queryData.program) {
			return (
				<NonIdealState
					title={<Labels.ProgramNotFoundTitle />}
					description={<Labels.ProgramNotFoundDesc />}
				/>
			);
		}

		return (
			<FragmentLayout
				layout={config.programLayout}
				fragments={programDisplayFragments}
				props={{
					program: queryData.program,
					renderTracks: this.renderTracks,
					renderBadges: this.renderBadges,
					renderPretest: this.renderPretest,
					renderPosttest: this.renderPosttest,
					expanded: this.state.expanded,
					toggleExpand: this.toggleExpand,
				}}
			/>
		);
	}

	private renderTracks = (tracks: ProgramTrack[]) => {
		const { config } = this.props;

		return (
			<RowsView viewKey="tracks.rows">
				{tracks.map((track) => (
					<FragmentLayout
						key={track.id}
						layout={config.trackLayout}
						fragments={trackDisplayFragments}
						props={{
							track,
							renderTrackCourses: this.renderTrackCourses}}
					/>
				))}
			</RowsView>
		);
	}

	private renderBadges = (badges: Badge[]) => {
		const { config } = this.props;

		return (
			<div>
				{
					badges.map((badge) => {

						return (
							<FragmentLayout
								key={badge.id}
								layout={config.badgeLayout}
								fragments={badgeDisplay}
								props={{badge}}
							/>
						);
					})
				}
			</div>
		);
	}

	private renderTrackCourses = (track: ProgramTrack) => {
		if (!track.products) {
			return null;
		}

		const courses = track.products.reduce((lst, tp) => {
			if (tp.course) {
				lst.push(tp.course);
			}
			return lst;
		}, []);

		return (
			<RowsView viewKey="courses.rows">
				{courses.map(this.renderCourse)}
			</RowsView>
		);

		// TODO use list renderer

		/*
		return (
			<AccordionView
				viewKey="courseList.accordion"
				items={courses}
				renderItem={this.renderCourse}
				renderExpand={this.renderCourseExpand}
				getId={(course) => course.id}
			/>
		);
		*/
	}

	private renderPretest = (course: Course) => {
		const { config } = this.props;

		return (
			<CardView viewKey="pretest.card">
				<FragmentLayout
					layout={config.preTestLayout}
					fragments={testFragments}
					props={{actions: catalogActions, course}}
				/>
			</CardView>
		);
	}

	private renderPosttest = (course: Course) => {
		const { config } = this.props;

		return (
			<CardView viewKey="posttest.card">
				<FragmentLayout
					layout={config.postTestLayout}
					fragments={testFragments}
					props={{actions: catalogActions, course}}
				/>
			</CardView>
		);
	}

	private renderCourse = (course: Course) => {
		const { config } = this.props;

		return (
			<CardView viewKey="course.card" interactive={true}>
				<FragmentLayout
					layout={config.courseLayout}
					fragments={trackProductFragments}
					props={{actions: catalogActions, course}}
				/>
			</CardView>
		);
	}

	private renderCourseExpand = (course: Course) => {
		const { actions, config } = this.props;

		return (
			<FragmentLayout
				layout={config.courseExpandLayout}
				fragments={trackProductFragments}
				props={{actions, course}}
			/>
		);
	}

	private toggleExpand = (key: string) => this.setState({expanded: { ...this.state.expanded, [key]: !this.state.expanded[key] }});
}



// WEBPACK FOOTER //
// ./src/panels/catalog/ProgramDetails.panel.tsx